import React, { useEffect, useState } from "react";

import { useLanguage } from "../../core/translations/LanguageContext";
import { SupportedLanguage } from "../../core/translations/supportedLanguages";
import { parseBackendDate } from "../../core/ServerApiLayer/dates";
import {
    getMaintenanceStatus,
    MaintenanceStatus,
} from "../../core/ServerApiLayer/VOne/maintenance/getStatus";
import { MaintenanceBannerTranslations } from "./translations/types";
import { enMaintenanceBannerTranslations } from "./translations/en";
import { deMaintenanceBannerTranslations } from "./translations/de";
import {
    BannerStrip,
    BannerMessage,
    DismissButton,
} from "./MaintenanceBanner.styles";

const TRANSLATIONS: Record<SupportedLanguage, MaintenanceBannerTranslations> = {
    en: enMaintenanceBannerTranslations,
    de: deMaintenanceBannerTranslations,
};

const DISMISSED_KEY = "cm.maintenance.dismissed";

/*
A bare `toLocaleString(language)` resolves "en" to en-US, which prints an
American date and a seconds field on a product served in the UK and Germany.
The locale and the fields are stated explicitly instead.
 */
const WINDOW_LOCALES: Record<SupportedLanguage, string> = {
    en: "en-GB",
    de: "de-DE",
};

const WINDOW_FORMAT: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    // Named because the time is rendered in the reader's own zone: without it a
    // student and an admin in different countries read the same sentence as two
    // different instants.
    timeZoneName: "short",
};

/*
One end of the window, in the reader's own zone and locale.

@value: the backend timestamp, zone-marked.
@language: the reader's chosen language.
 */
export function formatWindowTime(
    value: string,
    language: SupportedLanguage,
): string {
    return parseBackendDate(value).toLocaleString(
        WINDOW_LOCALES[language],
        WINDOW_FORMAT,
    );
}

/*
Identifies one appearance of the banner. The start timestamp means a new window
reappears after an old one was dismissed; the state means the "happening now"
message gets its own single appearance rather than inheriting the dismissal of
the announcement.
 */
function appearanceKey(status: MaintenanceStatus): string {
    return `${status.starts_at}:${status.state}`;
}

/*
Announces a scheduled maintenance window, and says when it is open.

Fetched once per mount, with no polling: this renders only on entry screens, so
nobody is on the page long enough for an interval to earn its requests. An admin
who sits here across the window's start is told by the 503 when they log in.
 */
export const MaintenanceBanner: React.FC = () => {
    const { language } = useLanguage();
    const text = TRANSLATIONS[language];
    const [status, setStatus] = useState<MaintenanceStatus | null>(null);
    const [dismissed, setDismissed] = useState<string | null>(() =>
        localStorage.getItem(DISMISSED_KEY),
    );

    useEffect(() => {
        let live = true;
        const load = async () => {
            const response = await getMaintenanceStatus();
            if (live && response.data) {
                setStatus(response.data);
            }
        };
        load();
        return () => {
            live = false;
        };
    }, []);

    if (
        !status ||
        status.state === "none" ||
        !status.starts_at ||
        !status.ends_at
    ) {
        return null;
    }
    if (dismissed === appearanceKey(status)) {
        return null;
    }

    // Rendered in the reader's own zone: a 22:00 UTC window is 06:00 the next
    // morning at +8, and the banner has to say so in their terms.
    const starts = formatWindowTime(status.starts_at, language);
    const ends = formatWindowTime(status.ends_at, language);

    const active = status.state === "active";

    const onDismiss = () => {
        const key = appearanceKey(status);
        localStorage.setItem(DISMISSED_KEY, key);
        setDismissed(key);
    };

    return (
        <BannerStrip role="status" active={active}>
            <BannerMessage>
                {active ? text.active(ends) : text.upcoming(starts, ends)}
            </BannerMessage>
            <DismissButton
                type="button"
                active={active}
                aria-label={text.dismiss}
                onClick={onDismiss}
            >
                ×
            </DismissButton>
        </BannerStrip>
    );
};
